const STORAGE_KEY = 'rcsim-sidebar-width';
const COLLAPSE_KEY = 'rcsim-sidebar-collapsed';
const COLLAPSED_CLASS = 'sidebar-collapsed';
const DEFAULT_W = 292;
const MIN_W = 200;
const MAX_W = 640;

let applyCollapsedState: ((collapsed: boolean) => void) | null = null;

export function setSidebarCollapsed(collapsed: boolean): void {
  applyCollapsedState?.(collapsed);
}

/** Drag handle on the left edge of #sidebar to change its width, plus collapse toggle. */
export function enableSidebarResize(): void {
  const app = document.getElementById('app')!;
  const sidebar = document.getElementById('sidebar')!;

  const handle = document.createElement('div');
  handle.id = 'sidebar-resizer';
  handle.title = 'Drag to resize';
  sidebar.appendChild(handle);

  let header = document.getElementById('sidebar-header');
  if (!header) {
    header = document.createElement('div');
    header.id = 'sidebar-header';
    sidebar.prepend(header);
  }

  const toggle = document.createElement('button');
  toggle.id = 'sidebar-toggle';
  toggle.type = 'button';
  header.appendChild(toggle);

  applyCollapsedState = (collapsed: boolean) => {
    app.classList.toggle(COLLAPSED_CLASS, collapsed);
    const label = collapsed ? 'Show side panel' : 'Hide side panel';
    toggle.title = label;
    toggle.setAttribute('aria-label', label);
    toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      // ignore
    }
  };

  applyWidth(app, restoreWidth());
  applyCollapsedState(restoreCollapsed());

  toggle.addEventListener('click', () => {
    setSidebarCollapsed(!app.classList.contains(COLLAPSED_CLASS));
  });

  let dragging = false;

  handle.addEventListener('pointerdown', (e) => {
    if (app.classList.contains(COLLAPSED_CLASS)) return;
    e.preventDefault();
    e.stopPropagation();
    dragging = true;
    handle.setPointerCapture(e.pointerId);
    document.body.classList.add('resizing-sidebar');
  });

  handle.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    const w = clamp(window.innerWidth - e.clientX, MIN_W, maxWidth());
    applyWidth(app, w);
  });

  const end = (e: PointerEvent) => {
    if (!dragging) return;
    dragging = false;
    document.body.classList.remove('resizing-sidebar');
    try {
      handle.releasePointerCapture(e.pointerId);
    } catch {
      // already released
    }
    saveWidth(parseWidth(app));
  };
  handle.addEventListener('pointerup', end);
  handle.addEventListener('pointercancel', end);

  window.addEventListener('resize', () => {
    const w = clamp(parseWidth(app), MIN_W, maxWidth());
    applyWidth(app, w);
  });
}

function maxWidth(): number {
  return Math.min(MAX_W, Math.floor(window.innerWidth * 0.55));
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function applyWidth(app: HTMLElement, w: number): void {
  app.style.setProperty('--sidebar-width', `${Math.round(w)}px`);
}

function parseWidth(app: HTMLElement): number {
  const raw = getComputedStyle(app).getPropertyValue('--sidebar-width').trim();
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : DEFAULT_W;
}

function restoreWidth(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_W;
    const n = parseFloat(raw);
    if (!Number.isFinite(n)) return DEFAULT_W;
    return clamp(n, MIN_W, maxWidth());
  } catch {
    return DEFAULT_W;
  }
}

function saveWidth(w: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(Math.round(w)));
  } catch {
    // ignore
  }
}

function restoreCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}
